/**
 * @bash/shared/harness/subagent-lane — how a helper's stop reason becomes a lane status.
 *
 * ONE source, because there are two writers of the same fact: the server's durable
 * materializer (apps/server/src/services/message-subagent-lane.ts) and the web store's
 * live walk (apps/web/src/stores/handlers/subagentHandlers.ts). They are deliberately the
 * same shape so they cannot drift; before this, both inlined the same ternary and both
 * carried the same bug, which is exactly the drift the pairing exists to prevent.
 *
 * THE BUG: `failed` was mapped to `stopped` alongside every other non-completed reason.
 * `laneLabel('failed')` already reads "failed", but the card's red chip and its
 * `errorCount` are driven by the lane STATUS, so a genuinely failed helper was counted
 * under "stopped" and the failure never reached the error rail.
 *
 * The reason itself is ALWAYS kept on the block whatever this returns: the status says
 * which rail a lane belongs to, the reason says what to call it.
 */

/** The three terminal lane statuses an END can write. `running` and `paused` are not
 *  outcomes, so they are never produced here. */
export type SubagentLaneStatus = 'completed' | 'stopped' | 'error';

/**
 * The lane status for a `subagent:end` stop reason.
 *
 * - absent (the pre-walls contract) and `completed` → `completed`
 * - `failed` → `error` (the red rail: the child did not finish and it was not a choice)
 * - everything else (`stopped_by_user`, `stopped_by_parent`, `turn_cap`, `refused`, and
 *   any reason a later spec adds) → `stopped`, the settled-but-not-done rail
 *
 * Unknown reasons deliberately fall to `stopped`, not `error`: a lane whose reason this
 * build has never heard of is settled, and calling it a failure would page on a rename.
 */
export function laneStatusForStopReason(reason: string | undefined): SubagentLaneStatus {
  if (reason === undefined || reason === 'completed') return 'completed';
  if (reason === 'failed') return 'error';
  return 'stopped';
}

/** A lane in one of these statuses is TERMINAL: nobody is coming back to it, so a late or
 *  duplicate START must not reopen it and a reason-less END must not overwrite it. */
export function isTerminalLaneStatus(status: unknown): boolean {
  return status === 'completed' || status === 'error' || status === 'stopped';
}
